import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { getActivityIconMeta, type ActivityEvent } from '@/types/activity';
import './activity-list-item.css';

interface ActivityListItemProps {
  event: ActivityEvent;
  className?: string;
  /** Style Guide unread quince dot for activity since last visit */
  isNew?: boolean;
}

const ActivityListItem: React.FC<ActivityListItemProps> = ({ event, className = '', isNew = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { Icon, accentClass } = getActivityIconMeta(event.action);
  const isClickable = Boolean(event.navigationPath);

  const handleClick = () => {
    if (event.navigationPath) {
      navigate(event.navigationPath);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isClickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const content = (
    <>
      <span className={`activity-item__icon ${accentClass}`} aria-hidden>
        <Icon width={16} height={16} />
      </span>
      <div className="activity-item__body">
        <div className="activity-item__summary">{event.summary}</div>
        <div className="activity-item__meta">{formatRelativeTime(event.createdAt)}</div>
      </div>
      {isNew && <span className="activity-item__new-dot" title={t('newSinceLastVisit')} aria-label={t('new')} />}
    </>
  );

  const classes =
    `activity-item ${isClickable ? 'activity-item--clickable' : ''} ${isNew ? 'activity-item--new' : ''} ${className}`.trim();

  if (isClickable) {
    return (
      <button type="button" className={classes} onClick={handleClick} onKeyDown={handleKeyDown}>
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
};

export default ActivityListItem;
